import Api from "@/api/api";
import {ModalContext} from "@/contexts";
import {Form, Modal} from "@/shared";
import {ChangeEvent, useContext, useState} from "react";
import {toast} from "react-toastify";
import "./AddSuperAdminCoachModal.scss";

type FormErrors = {
  email?: string;
  firstName?: string;
  lastName?: string;
};

type FormValueType = {
  email: string;
  firstName: string;
  lastName: string;
};

type Props = {
  selectedOrganizationID: string;
  updateCoaches: () => void;
};

export function AddSuperAdminCoachModal({selectedOrganizationID, updateCoaches}: Props) {
  const {closeModal} = useContext(ModalContext);

  const [formError, setFormError] = useState<FormErrors>({});

  const [formValues, setFormValues] = useState<FormValueType>({
    email: "",
    firstName: "",
    lastName: "",
  });

  const [loading, setLoading] = useState(false);

  const {email, firstName, lastName} = formValues;

  const validate = () => {
    const errors: FormErrors = {};
    const requiredFields: Partial<keyof FormErrors>[] = [
      "email",
      "firstName",
      "lastName"
    ];
    requiredFields.forEach((field) => {
      if (!formValues[field]) {
        errors[field] = "Required field";
      }
    });

    if (Object.keys(errors).length) {
      return errors;
    }

    if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9-]+.+.[A-Z]{2,4}$/i.test(email)) {
      errors.email = "Invalid email address";
      return errors;
    }
  };

  const handleFormUpdate = (event: ChangeEvent<HTMLInputElement>) => {
    const {value, name} = event.target;

    setFormValues({...formValues, [name]: value});
  };

  const addCoach = async () => {
    const errors = validate();
    if (errors) {
      setFormError(errors);
      return;
    }

    try {
      setLoading(true);
      const requestData = {
        email: formValues.email,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        name: `${formValues.firstName} ${formValues.lastName}`,
      };

      const res = await Api.post(
        `/v2/admin/organizations/${selectedOrganizationID}/coaches`,
        requestData
      );
      if (res.status === 200) {
        toast.success("Coach added successfully");
        updateCoaches();
        closeModal();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Some error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal className="add-superadmin-coach-modal">
      <Modal.Header text="Add Coach"/>
      <Form className="add-superadmin-coach-modal__form">
        <Form.Row>
          <Form.Label>First Name</Form.Label>
          <Form.Input
            value={firstName}
            onChange={handleFormUpdate}
            errorMessage={formError.firstName}
            name="firstName"
            placeholder="Enter first name"
          />
        </Form.Row>
        <Form.Row>
          <Form.Label>Last Name</Form.Label>
          <Form.Input
            value={lastName}
            onChange={handleFormUpdate}
            errorMessage={formError.lastName}
            name="lastName"
            placeholder="Enter last name"
          />
        </Form.Row>
        <Form.Row>
          <Form.Label>Email</Form.Label>
          <Form.Input
            value={email}
            errorMessage={formError.email}
            onChange={handleFormUpdate}
            name="email"
            placeholder="example@gmail.com"
          />
        </Form.Row>
      </Form>

      <Modal.Footer>
        <Form.Button type="outline" onClick={closeModal}>
          CANCEL
        </Form.Button>
        <Form.Button loading={loading} onClick={addCoach}>
          ADD COACH
        </Form.Button>
      </Modal.Footer>
    </Modal>
  );
}
